import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { ITcProductRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useParams } from "react-router-dom";

interface Option {
  value: string;
  label: string;
}

interface RawMaterialsInfoProps {
  formRef: React.MutableRefObject<any>;
  rawMaterialInfoData: ITcProductRawMaterialInformation[];
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<ITcProductRawMaterialInformation[]>
  >;
  onValidationChange?: (isValid: boolean) => void; // Callback to notify parent about validation status
}

const RawMaterialInfo: React.FC<RawMaterialsInfoProps> = ({
  formRef,
  rawMaterialInfoData,
  setRawMaterialInfoData,
  onValidationChange,
}) => {
  const [data, setData] = useState<ITcProductRawMaterialInformation[]>(
    rawMaterialInfoData || []
  );
  const { notify } = useNotification();
  const { updateField } = useFormActions("certified-products");
  const { setRestrictPopUpSave, setRawMtrlCodes, isRestrictPopUpSave } =
    useTransactionCertificateStore();
  const { TE_ID } = useParams();
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterials, setRawMaterials] = useState<Option[]>([]);
  const [groupedRawMaterials, setGroupedRawMaterials] = useState<
    { label: string; options: Option[] }[]
  >([]);
  const [rawMaterialsLoading, setRawMaterialsLoading] = useState<boolean>(false);

  // Calculate total percentage from data (excluding empty rows)
  const calculateTotalPercentage = (dataArray: ITcProductRawMaterialInformation[]): number => {
    return dataArray.reduce((sum, item) => {
      // Only count rows that have a code or percentage > 0
      if (item.tcProductRawMaterialCode || (item.tcProductRawMaterialPercentage ?? 0) > 0) {
        return sum + (item.tcProductRawMaterialPercentage || 0);
      }
      return sum;
    }, 0);
  };

  // Check if total is exactly 100%
  const isTotalOneHundred = (): boolean => {
    const total = calculateTotalPercentage(data);
    return total === 100;
  };

  // Check if total exceeds 100%
  const isTotalExceedsHundred = (): boolean => {
    const total = calculateTotalPercentage(data);
    return total > 100;
  };

  // Get current total percentage
  const getTotalPercentage = (): number => {
    return calculateTotalPercentage(data);
  };

  const validationSchema = Yup.object().shape({
    tcProductRawMaterialCode: Yup.string()
      .required("Product Raw Material Code is required")
      .test(
        "unique-raw-material-code",
        "Product Raw Material Code must be unique",
        function (value) {
          if (!value) return true;

          // Ensure the selected code is unique across all rows except the one being edited
          const isDuplicate = data.some((item, index) => {
            if (index === editingRowIndex) return false;
            return item.tcProductRawMaterialCode === value;
          });

          return !isDuplicate;
        }
      ),
    tcProductRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .required("Percentage is required")
      .positive("Percentage must be positive")
      .max(100, "Percentage cannot exceed 100")
      .test(
        'percentage-max-5-decimals',
        'Only 5 decimal places allowed',
        function (value) {
          if (value != null) {
            const decimals = (String(value).split('.')[1] || '').length;
            return decimals <= 5;
          }
          return true;
        }
      )
      .test(
        'percentage-total-not-exceed-100',
        'Total raw material percentage cannot exceed 100%',
        function (value) {
          if (editingRowIndex > -1 && value != null) {
            let total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              // Only count rows that have a code or percentage > 0
              if (item.tcProductRawMaterialCode || (item.tcProductRawMaterialPercentage ?? 0) > 0) {
                return sum + (item.tcProductRawMaterialPercentage || 0);
              }
              return sum;
            }, 0);
            return total + value <= 100;
          }
          return true;
        }
      ),
    tcProductRawMaterialUserSpecificTerm: Yup.string()
      .when("tcProductRawMaterialCode", {
        is: (val: string) => val === "RM0262",
        then: (schema) => schema.required("Product Raw Material User Specific Term is required when Raw Material is 'Others'"),
        otherwise: (schema) => schema.nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
  });

  const formik = useFormik<ITcProductRawMaterialInformation>({
    initialValues: {
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: 0,
      tcProductRawMaterialUserSpecificTerm: "",
      tcProductRawMaterialCodeDescription:""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formik,
        editingRowIndex,
        hasUnsavedRow: editingRowIndex !== -1, // Track if a row is being edited
      };
    }
  }, [formRef, formik, editingRowIndex]);

  useEffect(() => {
    setData(Array.isArray(rawMaterialInfoData) ? rawMaterialInfoData : []);
  }, [rawMaterialInfoData]);

  // Notify parent when data changes about validation status
  useEffect(() => {
    if (onValidationChange) {
      // Trigger validation change callback when data changes
      onValidationChange(!isTotalExceedsHundred());
    }
  }, [data, onValidationChange]);

  useEffect(() => {
    const getRawMaterialCodes = async () => {
      setRawMaterialsLoading(true);
      try {
        const result: any = await dtrackItService.getRawMaterialCodes(
          String(TE_ID || "")
        );
        const scRmCodes = Array.isArray(result?.data?.["sc-rmcode"])
          ? result.data["sc-rmcode"].map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];

        const rmCodes = Array.isArray(result?.data?.rmcodes)
          ? result.data.rmcodes.map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];

        const legacyFlatList =
          !scRmCodes.length && !rmCodes.length && Array.isArray(result?.data)
            ? result.data.map((t: any) => ({
                value: t.code_1,
                label: `${t.description_1} (${t.code_1})`,
              }))
            : [];

        const combined = [...scRmCodes, ...rmCodes, ...legacyFlatList];

        setRawMaterials(combined);
        setGroupedRawMaterials(
          [
            { label: "SC Raw Material Codes", options: scRmCodes },
            { label: "Other Raw Material Codes", options: rmCodes },
          ].filter((group) => group.options.length > 0)
        );
        setRawMtrlCodes(combined);
      } catch (err) {
        Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
        notify("Failed to fetch raw material codes", "error");
      } finally {
        setRawMaterialsLoading(false);
      }
    };
    if (TE_ID) {
      getRawMaterialCodes();
    }
  }, [TE_ID]);

  const displayedRawMaterials: Option[] = rawMaterialsLoading
    ? [{ value: "__loading__", label: "Loading..." }]
    : rawMaterials;

  const displayedGroupedRawMaterials =
    rawMaterialsLoading || groupedRawMaterials.length === 0
      ? undefined
      : groupedRawMaterials;

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value || "" } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: ITcProductRawMaterialInformation = {
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: 0,
      tcProductRawMaterialUserSpecificTerm: "",
      tcProductRawMaterialCodeDescription:""
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    // If editing a newly added row, remove it
    if (editingRowIndex === data.length - 1) {
      const cleanData = data.slice(0, -1);
      setData(cleanData);
      setRawMaterialInfoData(cleanData);
      updateField("rawMaterialInfo", cleanData); // Update global store
    }
  
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false; // Reset unsaved state
  };

  const handleSaveRow = async (): Promise<boolean> => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((err) => notify(err as string, "error"));
      return false;
    }
  
    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
      index === editingRowIndex ? updatedRow : row
    );
  
    // Save locally
    setData(newData);
  
    // Push into parent stepData
    setRawMaterialInfoData(newData);
  
    // Push into global store
    updateField("rawMaterialInfo", newData);
  
    // Reset editing state
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  
    // Explicitly mark that there are no unsaved changes
    (formik as any).hasUnsavedRow = false; // Add this to track unsaved state if needed
  
    notify("Row saved successfully", "success");
    return true;
  };
  
  

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
  
    setData(updatedData);
  
    //  Push into parent & global
    setRawMaterialInfoData(updatedData);
    updateField("rawMaterialInfo", updatedData);
  
    notify("Row deleted successfully", "success");
  };
  

  const columns: EditableColumnDef<ITcProductRawMaterialInformation>[] = [
    {
      accessorKey: "tcProductRawMaterialCode",
      header: "Product Raw Material Code",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Product Raw Material Code
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          label=""
          name="tcProductRawMaterialCode"
          value={value}
          options={displayedRawMaterials}
            groupedOptions={displayedGroupedRawMaterials}
          isOptionDisabled={(option:any)=> rawMaterialsLoading || option?.value === "__loading__"}
          enableSearch={true}
          placeholder="Select Raw Material Code"
          onChange={(e) => {
            const selectedCode = e.target.value;
            const selectedMaterial = rawMaterials.find(
              (mat) => mat.value === selectedCode
            );
    
            const description = selectedMaterial
              ? selectedMaterial.label.split(" (")[0]
              : "";
    
            //  Update both fields
            onChange(selectedCode);
            formik.setFieldValue("tcProductRawMaterialCodeDescription", description);
            handleCellChange(rowIndex, "tcProductRawMaterialCodeDescription", description);
          }}
        />
      ),
    },           
    {
      accessorKey: "tcProductRawMaterialPercentage",
      header: "Product Raw Material Percentage",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Product Raw Material Percentage
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductRawMaterialPercentage"
          value={value}
          inputProps={{ max: 100, step: "0.00001" }}
          type="number"
          onChange={(e) => {
            const raw = e.target.value;
            // Allow empty
            if (raw === "" || raw === null || raw === undefined) {
              onChange(raw);
              return;
            }

            // Accept numbers with up to 5 decimal places and value between 0 and 100
            const matched = String(raw).match(/^\d*(?:\.(\d{0,5})?)?$/);
            const num = Number(raw);
            if (matched && !Number.isNaN(num) && num >= 0 && num <= 100) {
              onChange(raw);
            }
          }}
          placeholder="Enter value (max 5 decimals)"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialUserSpecificTerm",
      header: "Product Raw Material User Specific Term",
      Header: () => {
        const isRequired = formik.values.tcProductRawMaterialCode === "RM0262";
        return (
          <span>
            {isRequired && <i style={{ color: "red" }}>*</i>} Product Raw Material User Specific Term
          </span>
        );
      },
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductRawMaterialUserSpecificTerm"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder="Enter Raw Material User Specific Term"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 1,
        border: "2px solid rgba(128, 128, 128, .2)",
        my: 4,
      }}
    >
      <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
        <StyledButton
          disabled={isRestrictPopUpSave || isTotalOneHundred()} 
          onClick={handleAddNewRow}
          sx={{ mb: 2 }}
          title={isTotalOneHundred() ? "Total percentage has reached 100%" : "Add new raw material entry"}
        >
          Add Raw Material
        </StyledButton>
      </Box>
      <EditableTable
        data={data} 
        columns={columns}
        onSaveRow={handleSaveRow}
        onCellChange={handleCellChange}
        editingRowIndex={editingRowIndex}
        onEditRow={handleEditRow}
        onCancelEdit={handleCancelEdit}
        onDeleteRow={handleDeleteRow}
      />
    </Box>
  );
};

export default RawMaterialInfo;
